// waiting for dom nodes to emit promotions events, then send them to google analytics

/**
 * Remove properties with null, undefined, and empty-string values from an object.
 * Empty strings are stripped too so nav levels whose menu handle is blank
 * (Liquid renders data-nav-l2/l3="") don't reach GA as empty params, which it
 * displays inconsistently vs. a genuinely absent level. Matches the .filter(Boolean)
 * used for nav_path in clickNavHandler.
 * @param {Record<string, any>} obj
 * @returns {Record<string, any>}
 */
const stripEmptyValues = (obj) => {
	return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null && v !== ''));
};

/**
 * Returns true if the given string is a valid absolute URL.
 * @param {string} string
 * @returns {boolean}
 */
const isUrl = (string) => {
	try {
		new URL(string);
		return true;
	} catch (e) {
		return false;
	}
};

/**
 * Shopify product pages have json endpoints – append '.json' to the url
 * @param { string } productUrl could be a slug eg '/products/00008-needed-most-fund' or a full url
 * @returns { Promise<Record<string, any>> }
 */
async function getShopifyProductData(productUrl) {
	if (!productUrl) return;
	try {
		const res = await fetch(`${productSlug}.json`);
		if (!res.ok) throw new Error(res?.statusText ? res.statusText : 'Could not fetch product data for ' + productUrl);
		const data = await res.json();
		if ('product' in data) {
			return data.product;
		} else {
			throw new Error('No product data found for ' + productUrl);
		}
	} catch (error) {
		console.error(error.message);
	}
}

/**
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=4633702010139566524-NA&client_type=gtag#view_promotion
 * @see https://developers.google.com/analytics/devguides/collection/gtagjs/enhanced-ecommerce#measure_promotion_clicks
 * @typedef { Object } PromotionData
 * @property { string= } creative_name
 * @property { string } creative_slot
 * @property { string= } promotion_id
 * @property { string } promotion_name
 * @property { {item_id: string, item_name: string}[]= } items
 */

/**
 * Extract promotion-relevant data from the custom element's JSON data
 * @param { Record<string, any> } contentData
 * @returns { PromotionData }
 */
function getPromotionData(contentData) {
	const { creative_name, creative_slot, promotion_id, promotion_name, item_id, item_name } = contentData;
	/** @type { PromotionData } */
	let promotionData = {
		creative_name,
		creative_slot,
		promotion_id,
		promotion_name
	};
	if (item_id && item_name) {
		promotionData.items = [
			{
				item_id,
				item_name
			}
		];
	}
	return stripEmptyValues(promotionData);
}

/**
 * Returns last bit of pathname, which is the product slug on shopify links
 * @see https://stackoverflow.com/a/40725409
 * @param { string } url
 * @returns { string }
 */
function getSlug(url) {
	return new URL(url).pathname.split('/').pop() || '';
}

/**
 * @param { string= } promotion_name
 * @returns { boolean }
 */
function ifAlreadyBeenViewed(promotion_name) {
	if (!promotion_name) return false;
	const brand = document.body.dataset.brand;
	let key = 'CUSA: ' + brand.toUpperCase() + ' viewed promotions for ' + new Date().toISOString().slice(0, 10);
	if (!sessionStorage.getItem(key)) {
		sessionStorage.setItem(key, JSON.stringify([promotion_name]));
		return false;
	} else {
		let viewedPromotions = JSON.parse(sessionStorage.getItem(key));
		if (viewedPromotions.includes(promotion_name)) return true;
		viewedPromotions.push(promotion_name);
		sessionStorage.setItem(key, JSON.stringify(viewedPromotions));
	}
	return false;
}

/**
 * @example
 * if (isElementInViewport(document.getElementById('wotcha'))) rejoice();
 * @see https://stackoverflow.com/a/7557433
 * @param { Element } el
 * @returns { boolean }
 */
function isElementInViewport(el) {
	const rect = el.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth) /* or $(window).width() */
	);
}

/**
 * Pushing all events to Elevar's data layer when we're in Shopify, which we are
 * (though we're keeping the alternative methods in case Elevar doesn't test well)
 * @param { string } eventName
 * @param { Record<string, any> } data
 * @param { boolean } inShopify
 * @returns { void }
 */
function sendAnalyticsEvent(eventName, data = {}, inShopify = true) {
	if (eventName.includes('promotion')) {
		if ('view_promotion' === eventName) {
			const el = 'content_id' in data && document.getElementById(data.content_id);
			if (el && !isElementInViewport(el)) return;
			if (ifAlreadyBeenViewed(data.promotion_name)) return;
		}
		data = getPromotionData(data);
	}
	data = stripEmptyValues(data);
	if (inShopify || eventName.startsWith('dl_')) {
		data.event = eventName;
		window.ElevarDataLayer = window.ElevarDataLayer ?? [];
		window.ElevarDataLayer.push(data);
	} else {
		window.gtag('event', eventName, data);
	}
	console.log('[Analytics]', eventName, data);
}

/**
 * Pack up and send promotions data from a cusa-content element
 * @param { MouseEvent | TouchEvent } event
 */
function clickPromotionHandler(event) {
	const cusaContent = event.target.closest('cusa-content');
	if (!cusaContent) return;
	// this fails because the contents of the custom el cannot be accessed:
	// const contentDataScript = cusaContent.querySelector('[id$="_contentData"]');
	// console.error(JSON.stringify(contentDataScript));
	const contentData = cusaContent?.getJsonData();
	if (!contentData) return;
	sendAnalyticsEvent('select_promotion', contentData);
}

/**
 * Build and send a nav_click event from a clicked nav element's data attributes.
 * Liquid emits the menu handle and group on the nav container (data-nav-name,
 * data-nav-group) and the level handles on each clickable item (data-nav-l1..l3);
 * see snippets/cusa-universal-nav.liquid and the header-*-menu / header-drawer snippets.
 * sendAnalyticsEvent() strips empty values, so absent l2/l3 drop out. nav_path is
 * always sent (even a single-level click) so every click shows in a nav_path report.
 * nav_action flags intent off the clicked element's tag: a <summary> is a parent
 * disclosure that opens a submenu (open), while an <a> genuinely navigates to a page
 * (navigate). This holds across all four render modes — dropdown, mega, drawer, and
 * Universal nav — since Liquid only ever puts data-nav-l1 on those two element types.
 * @param { MouseEvent } event
 */
function clickNavHandler(event) {
	const link = event.target.closest('[data-nav-l1]');
	if (!link) return;
	const nav = link.closest('[data-nav-name]');
	if (!nav) return;
	const levels = [link.dataset.navL1, link.dataset.navL2, link.dataset.navL3].filter(Boolean);
	sendAnalyticsEvent('nav_click', {
		site_name: (document.body.dataset.brand || '').toUpperCase(),
		nav_group: nav.dataset.navGroup,
		nav_name: nav.dataset.navName,
		nav_l1: link.dataset.navL1,
		nav_l2: link.dataset.navL2,
		nav_l3: link.dataset.navL3,
		nav_path: levels.length ? levels.join(' > ') : undefined,
		nav_action: link.tagName === 'SUMMARY' ? 'open' : 'navigate'
	});
}

/**
 * Hang out and wait for certain events to be emitted by custom elements;
 * also listen for clicks on <cusa-content> links
 */
function addAnalyticsEventListeners() {
	const eventNames = ['view_promotion', 'select_promotion', 'charitable_click'];
	eventNames.forEach((eventName) => {
		document.addEventListener(eventName, (event) => {
			sendAnalyticsEvent(eventName, event.detail);
		});
	});
	/**
	 * <cusa-content> custom scheduled-content elements
	 * @type { NodeListOf<HTMLElement> }
	 */
	const cusaContents = document.querySelectorAll('cusa-content');
	cusaContents.forEach((cusaContent) => {
		cusaContent.addEventListener('click', (event) => {
			clickPromotionHandler(event);
		});
	});
	// Delegated listener for nav clicks (universal nav + focus nav, all render modes)
	document.addEventListener('click', clickNavHandler);
}

/**
 * we're not waiting for 'complete' === document.readyState or similar; if we do,
 * we miss the first promotions event emitted by the custom elements on render
 *
 * window.addEventListener('DOMContentLoaded', addAnalyticsEventListeners, { once: true });
 */
addAnalyticsEventListeners();
